'use client';

import PropTypes from 'prop-types';
import { usePathname, useRouter } from 'next/navigation';
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';

const CHARCOAL = '#0a0a0a';
const MUTED = 'rgba(10, 10, 10, 0.55)';

export function formatCollectionSubNavLabel(label) {
  return String(label || '')
    .replace(/\s+Collection$/i, '')
    .trim();
}

export function normalizeCollectionSubNavPath(path) {
  const stripped = String(path || '').split(/[?#]/)[0];
  if (stripped.length > 1 && stripped.endsWith('/')) {
    return stripped.slice(0, -1);
  }
  return stripped;
}

export function isCollectionSubNavActive(pathname, href) {
  const current = normalizeCollectionSubNavPath(pathname);
  const target = normalizeCollectionSubNavPath(href);
  return Boolean(current && target && current === target);
}

const selectSx = {
  bgcolor: 'transparent',
  borderRadius: 0,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: 0.2,
  color: CHARCOAL,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: CHARCOAL,
    borderWidth: 1,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: CHARCOAL,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: CHARCOAL,
    borderWidth: 1,
  },
  '& .MuiSelect-icon': {
    color: CHARCOAL,
  },
};

const menuPaperSx = {
  bgcolor: 'background.default',
  backgroundImage: 'none',
  border: `1px solid ${CHARCOAL}`,
  borderRadius: 0,
  boxShadow: 'none',
  mt: 0.5,
};

const menuItemSx = {
  fontSize: 14,
  fontWeight: 500,
  color: CHARCOAL,
  '&.Mui-selected': {
    bgcolor: 'rgba(10, 10, 10, 0.08)',
    fontWeight: 700,
  },
  '&.Mui-selected:hover': {
    bgcolor: 'rgba(10, 10, 10, 0.12)',
  },
  '&:hover': {
    bgcolor: 'rgba(10, 10, 10, 0.06)',
  },
};

export default function CollectionSubNavLinks({ collectionName, links }) {
  const pathname = usePathname() || '';
  const router = useRouter();

  if (!links?.length) return null;

  const selectedHref =
    links.find(link => isCollectionSubNavActive(pathname, link.href))?.href || '';
  const navLabel = `${collectionName} sub-collections`;

  return (
    <Box component="nav" aria-label={navLabel} sx={{ mt: 4 }}>
      <Typography
        component="p"
        sx={{
          mb: 1,
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: 0.4,
          color: MUTED,
        }}
      >
        Browse By sub-collection
      </Typography>
      <FormControl size="small" sx={{ width: '100%', maxWidth: 360 }}>
        <Select
          displayEmpty
          value={selectedHref}
          onChange={event => {
            const href = event.target.value;
            if (href && !isCollectionSubNavActive(pathname, href)) {
              router.push(href);
            }
          }}
          renderValue={value => {
            const selected = links.find(link => link.href === value);
            return selected ? formatCollectionSubNavLabel(selected.label) : 'Select';
          }}
          inputProps={{ 'aria-label': navLabel }}
          sx={selectSx}
          MenuProps={{
            PaperProps: { sx: menuPaperSx },
          }}
        >
          {selectedHref === '' ? <MenuItem value="" sx={{ display: 'none' }} /> : null}
          {links.map(link => (
            <MenuItem key={link.href} value={link.href} sx={menuItemSx}>
              {formatCollectionSubNavLabel(link.label)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

CollectionSubNavLinks.propTypes = {
  collectionName: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
};
